import AlarmsTableFactory from "../../factories/AlarmsTableFactory";
import SignalStatusEnum from "@/constants/enums/SignalStatusEnum";
import AlarmManagementStatus from "@/constants/enums/AlarmManagementStatus";
import i18n from "@/i18n";

const getters = {
	/**
	 * Retorna a configuração das colunas da tabela de notificações.
	 *
	 * @returns {Array<Object>} Configuração das colunas da tabela.
	 */
	headers()
	{
		return AlarmsTableFactory.HeaderTableView();
	},

	/**
	 * Retorna a exibição formatada para um determinado status.
	 *
	 * @param {string} status - Status da notificação.
	 * @returns {string} Texto formatado do status.
	 */
	statusDisplay: () => (status) =>
	{
		return AlarmsTableFactory.GetStatusDisplay(status);
	},

	/**
	 * Verifica se todos as turbinas disponíveis estão selecionadas.
	 *
	 * @param {Object} state - O estado Vuex local.
	 * @returns {boolean} `true` se todos as turbinas estão selecionadas, caso contrário `false`.
	 */
	allAssetsSelected(state)
	{
		return state.selectedAssets.length === state.availableAssets.length;
	},

	/**
	 * Conta a quantidade de itens que foram dispensados (managementType igual a NONE).
	 * @param {Object} state - Estado contendo a lista de logs de ativos.
	 * @returns {number} Número de itens dispensados.
	 */
	dimissedItemsCount(state)
	{
		return state.logs.filter(asset => AlarmManagementStatus.NONE === asset.lastManagement.managementType).length;
	},

	/**
	 * Conta a quantidade de itens com relatório anexado (managementType igual a REPORT_ATTACHED).
	 * @param {Object} state - Estado contendo a lista de logs de ativos.
	 * @returns {number} Número de itens com relatório anexado.
	 */
	reportAttachedItemsCount(state)
	{
		return state.logs
			.filter(asset => AlarmManagementStatus.REPORT_ATTACHED === asset.lastManagement.managementType).length;
	},

	/**
	 * Retorna o ícone apropriado dependendo se todos as turbinas visíveis estão selecionadas ou não.
	 *
	 * @param {Object} state - O estado Vuex local.
	 * @param {Object} getters - Os getters Vuex local.
	 * @returns {string} Nome do ícone (ex: "mdi-close-box" ou "mdi-checkbox-blank-outline").
	 */
	selectAllFilteredAssetsIcon(getters)
	{
		return getters.allFilteredAssetsSelected ? "mdi-close-box" : "mdi-checkbox-blank-outline";
	},

	/**
	 * Retorna as turbinas disponíveis filtradas com base no input de texto do usuário.
	 *
	 * @param {Object} state - O estado Vuex local.
	 * @returns {Array<Object>} Lista de assets filtrados.
	 */
	filteredAssets(state)
	{
		if (!!state.searchQuery)
		{
			const query = state.searchQuery.toLowerCase();
			return state.availableAssets.filter(asset => asset.name.toLowerCase().includes(query));
		}
		return state.availableAssets;
	},

	/**
	 * Verifica se todos as turbinas filtradas estão selecionadas.
	 *
	 * @param {Object} state - O estado Vuex local.
	 * @param {Object} getters - Os getters Vuex.
	 * @returns {boolean} `true` se todos as turbinas filtradas estão selecionadas.
	 */
	allFilteredAssetsSelected(state, getters)
	{
		return (
			getters.filteredAssets.length > 0 &&
			getters.filteredAssets.every(filtered => state.selectedAssets.some(sel => sel.name === filtered.name))
		);
	},
	/**
     * Retorna a lista de status disponíveis no filtro.
     */
	availableStatus: (state) => state.availableStatus,

	/**
     * Retorna a lista de status selecionados no filtro.
     */
	selectedStatus: (state) => state.selectedStatus,

	/**
	 * Retorna a lista de status disponíveis no filtro já traduzidas.
	 */
	translatedAvailableStatus: () =>
	{
		return [
			{
				name: i18n.t("Labels.Alarm"),
				id:   SignalStatusEnum.ALARM
			},
			{
				name: i18n.t("Labels.Trip"),
				id:   SignalStatusEnum.TRIP,
			},
			{
				name: i18n.t("Labels.Undefined"),
				id:   SignalStatusEnum.UNDEFINED,
			}
		];
	},

	/**
	 * Retorna a lista de tratativas disponíveis no filtro.
	 */
	availableManagements: () =>
	{
		return [
			{
				name:  i18n.t("Labels.NoneTratative"),
				value: AlarmManagementStatus.NONE,
			},
			{
				name:  i18n.t("Labels.SeenTratative"),
				value: AlarmManagementStatus.SEEN,
			},
			{
				name:  i18n.t("Labels.DismissedTratative"),
				value: AlarmManagementStatus.DISMISSED,
			},
			{
				name:  i18n.t("Labels.QuarentineTratative"),
				value: AlarmManagementStatus.QUARENTINE,
			},
			{
				name:  i18n.t("Labels.ReportTratative"),
				value: AlarmManagementStatus.REPORT_ATTACHED,
			},
			{
				name:  i18n.t("Labels.ExpiredQuarentineTratative"),
				value: AlarmManagementStatus.EXPIRED_QUARENTINE,
			},
		];
	},

	/**
	 * Retorna as opções de quarentena disponíveis no diálogo.
	 */
	quarantineOptions: () =>
	{
		return [
			{ text: i18n.t("Buttons.24Hours"), value: 1 },
			{ text: i18n.t("Buttons.72Hours"), value: 3 },
			{ text: i18n.t("Buttons.7Days"), value: 7 },
			{ text: i18n.t("Buttons.15Days"), value: 15 },
			{ text: i18n.t("Buttons.1Month"), value: 30 },
			{ text: i18n.t("Buttons.3Months"), value: 90 },
		];
	},
};

export default { getters };
